import {
  Send, Mail, Whatsapp,
  Copy,
} from '@huspy/briks-icons';
import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import { toast } from '@huspy/forge/shared';
import { Divider } from '@huspy/styled-system/jsx';
import { Image } from '@mantine/core';
import { useClipboard, useDisclosure } from '@mantine/hooks';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import useRedraftDigitalDocument from '@modules/opportunities/entities/bankApplication/mutation/useRedraftDigitalDocument';
import useSendDigitalDocument from '@modules/opportunities/entities/bankApplication/mutation/useSendDigitalDocument';
import { ApplicantType } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import { imagesPath } from '@shared/css.const';
import { shareToWhatsapp } from '@shared/utils';
import { useTranslation } from 'react-i18next';

const mapApplicantType = (applicantType: ApplicantType) => {
  if (applicantType === 'co-applicant') {
    return 'coApplicant';
  }
  if (applicantType === 'co-borrower') {
    return 'coBorrower';
  }
  return applicantType;
};

export type ShareLinks = {
  fullName: string;
  applicantType: ApplicantType;
  link: string | null;
}[];

const baseTranslationPath = 'opportunity.caseSubmission.bankForms.bankApplication.esignatureSlido';
export const EsignatureSlido = ({
  isEdit,
  shareLinks,
  opportunityExternalId,
  bankApplicationExternalId,
  digitalBankApplicationId,
}: {
  isEdit: boolean;
  shareLinks: ShareLinks;
  opportunityExternalId: string;
  bankApplicationExternalId: string;
  digitalBankApplicationId: string;
}) => {
  const { t } = useTranslation();
  const [isOpen, { close, open }] = useDisclosure(false);
  const actionType = isEdit ? 'edit' : 'send';
  const { isFeatureActive } = useFeatureFlags();
  const isFeatureFlagOn = isFeatureActive('opportunities.esignature');
  const clipboard = useClipboard();
  const {
    mutateAsync: redraftDigitalDoc,
    isPending: redraftPending,
  } = useRedraftDigitalDocument(opportunityExternalId);
  const {
    mutateAsync: sendDigitalDoc,
    isPending: sendPending,
  } = useSendDigitalDocument(opportunityExternalId);

  const handleEdit = async () => {
    await redraftDigitalDoc({ bankApplicationExternalId, digitalDocumentId: digitalBankApplicationId });
    await sendDigitalDoc({
      bankApplicationExternalId,
      digitalDocumentId: digitalBankApplicationId,
    });
  };

  const getShareMessage = () => {
    const headerMessage = 'Please access the following links to sign your bank forms';

    if (shareLinks.length === 0) {
      return headerMessage;
    }

    const applicantLinks = shareLinks.map((shareLink) => `${shareLink.fullName}: ${shareLink.link}`);

    return [headerMessage, ...applicantLinks].join('\n');
  };

  const isLoading = redraftPending || sendPending;
  const renderBtn = () => {
    if (isEdit) {
      return (
        <Flex justify='end' gap='2'>
          <Button
            variant='secondary'
            type='button'
            data-testid='test-cancelBtn'
            onClick={ close }
          >
            {t(`${baseTranslationPath}.edit.cancelBtn`)}
          </Button>
          <Button
            type='button'
            data-testid='test-editBtn'
            loading={ isLoading }
            onClick={ handleEdit }
            variant={ isLoading ? 'secondary' : 'primary' }
          >
            {t(`${baseTranslationPath}.edit.editBtn`)}
          </Button>
        </Flex>
      );
    }
    return (
      <Flex justify='end' gap='2'>
        <Button type='button' data-testid='test-sendEmail' display='none'>
          <Mail color='white' />
          {t(`${baseTranslationPath}.send.sendEmail`)}
        </Button>
        <Button
          type='button'
          data-testid='test-shareWS'
          onClick={ () => {
            shareToWhatsapp(getShareMessage());
          } }
        >
          <Whatsapp color='white' />
          {t(`${baseTranslationPath}.send.shareWS`)}
        </Button>
      </Flex>
    );
  };

  const imageSrc = `${imagesPath}/${isEdit ? 'missing-fields.png' : 'send-esignature.svg'}`;

  const renderShareLinks = () => {
    if (isEdit) {
      return <></>;
    }

    return (
      <Flex direction='column' gap='3'>
        <Divider my='6' />
        <Text size='md' color='text-secondary'>
          {t(`${baseTranslationPath}.send.copyDisclaimer`)}
        </Text>
        <Flex gap='2'>
          {shareLinks.map((shareLink) => (
            <Button
              variant='secondary'
              key={ shareLink.applicantType }
              onClick={ () => {
                if (shareLink.link) {
                  toast('info', { message: t(`${baseTranslationPath}.send.urlCopied`) });
                  clipboard.copy(shareLink.link);
                }
              } }
            >
              <Text size='sm' fontWeight='medium'>
                {shareLink.fullName}
                {` (${t(`common.applicantType.${mapApplicantType(shareLink.applicantType)}`)})`}
              </Text>
              <Copy />
            </Button>
          ))}
        </Flex>
      </Flex>
    );
  };

  if (!isFeatureFlagOn) return null;
  return (
    <>
      <BaseSlido
        opened={ isOpen }
        close={ close }
        title={ (
          <Text size='3xl' px='4' py='8'>
            {t(`${baseTranslationPath}.${actionType}.header`)}
          </Text>
        ) }
        footer={ renderBtn() }
      >
        <Flex direction='column' h='100%' justify='space-between'>
          <Flex bg='white' p='4' borderRadius='4' direction='column' my='2'>
            <Box w='100%' mb='2'>
              <Image src={ imageSrc } w='100%' />
            </Box>
            <Text size='3xl' my='2'>
              {t(`${baseTranslationPath}.${actionType}.title`)}
            </Text>
            <Text size='md' my='2' color='text-secondary'>
              {t(`${baseTranslationPath}.${actionType}.description`)}
            </Text>
            {renderShareLinks()}
          </Flex>
        </Flex>
      </BaseSlido>
      <Button
        variant={ isEdit ? 'outline' : 'primary' }
        type='button'
        onClick={ open }
        data-testid='test-openBtn'
      >
        {!isEdit && <Send color='white' />}
        {t(`${baseTranslationPath}.${actionType}.openBtn`)}
      </Button>
    </>
  );
};
